import React, { useState, useCallback } from 'react';
import { Activity, CheckCircle, XCircle, Loader2, RefreshCw, Zap, Clock, Globe, Package, Box, Truck, Euro, BookOpen, Copy, Check, ChevronDown, ChevronRight, Webhook } from 'lucide-react';
import { Button } from '../components/ui/button';
import axiosInstance from '../utils/axiosInstance';

const API_BASE = 'https://api.shippy.lamptwist.be';

const ENDPOINT_GROUPS = [
  {
    category: 'Internal APIs',
    endpoints: [
      { name: 'Dashboard Stats', method: 'GET', url: 'api/dashboard/stats/', icon: Activity, description: 'Core dashboard metrics' },
      { name: 'Shipping Rates', method: 'GET', url: 'api/shipping-rates/', icon: Euro, description: 'Shipping rates overview' },
      { name: 'Packaging Data', method: 'GET', url: 'api/packaging-data/?limit=1', icon: Box, description: 'Product dimensions database' },
      { name: 'Packing Records', method: 'GET', url: 'api/packing-analysis-records/?limit=1', icon: Package, description: 'Packing analysis history' },
      { name: 'Shipping Rules', method: 'GET', url: 'api/shipping-rules/', icon: BookOpen, description: 'Oversized product rules' },
      { name: 'Benchmark Cases', method: 'GET', url: 'api/benchmark/test-cases/', icon: Zap, description: 'Benchmark test cases' },
    ],
  },
  {
    category: 'External Services',
    endpoints: [
      { name: 'Shopify Order Lookup', method: 'GET', url: 'api/orders/1001/', icon: Globe, description: 'Tests Shopify API connectivity (order #1001)' },
      { name: 'Odoo Delivery Orders', method: 'GET', url: 'api/odoo/delivery-orders/?limit=1', icon: Truck, description: 'Tests Odoo XML-RPC connectivity' },
    ],
  },
  {
    category: 'B2B Packing API',
    endpoints: [
      {
        name: 'B2B Packing Endpoint',
        method: 'POST',
        url: 'api/v1/packing/shippy-pack-sim-8f7a3b/',
        icon: Package,
        description: 'Public packing API (requires token)',
        isTokenBased: true,
        testPayload: {
          items: [{ variant_id: 'test', quantity: 1 }],
          country: 'Belgium',
        },
      },
    ],
  },
  {
    category: 'Webhooks',
    endpoints: [
      {
        name: 'Shopify Flow Webhook',
        method: 'POST',
        url: 'api/webhook/packing-analysis/',
        icon: Webhook,
        description: 'Shopify Flow packing analysis trigger (requires token)',
        isTokenBased: true,
        testPayload: { order_number: '#1001' },
      },
    ],
  },
];

// -- Copyable code block ---------------------------------------------------
const CodeBlock = ({ code, language = '' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group">
      <pre className="rounded-lg bg-zinc-950 dark:bg-zinc-900 text-zinc-100 text-xs leading-relaxed p-4 overflow-x-auto">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Copy"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
};

// -- Collapsible section ---------------------------------------------------
const DocSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left bg-card hover:bg-muted/40 transition-colors"
      >
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
        {Icon && <Icon className="h-4 w-4 text-primary shrink-0" />}
        <span className="text-sm font-semibold text-foreground">{title}</span>
      </button>
      {open && <div className="px-5 pb-5 pt-2 bg-card space-y-4 border-t border-border">{children}</div>}
    </div>
  );
};

// -- Param table -----------------------------------------------------------
const ParamTable = ({ params }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-border bg-muted/50">
          <th className="px-3 py-2 text-left font-semibold">Parameter</th>
          <th className="px-3 py-2 text-left font-semibold">Type</th>
          <th className="px-3 py-2 text-left font-semibold">Required</th>
          <th className="px-3 py-2 text-left font-semibold">Description</th>
        </tr>
      </thead>
      <tbody>
        {params.map((p, i) => (
          <tr key={i} className="border-b border-border">
            <td className="px-3 py-2 font-mono text-primary">{p.name}</td>
            <td className="px-3 py-2 font-mono text-muted-foreground">{p.type}</td>
            <td className="px-3 py-2">
              {p.required
                ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">Required</span>
                : <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">Optional</span>
              }
            </td>
            <td className="px-3 py-2 text-muted-foreground">{p.desc}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ApiHealth = () => {
  const [results, setResults] = useState({});
  const [testingAll, setTestingAll] = useState(false);
  const [b2bToken, setB2bToken] = useState('');

  const testEndpoint = useCallback(async (endpoint) => {
    const key = endpoint.url;
    setResults(prev => ({ ...prev, [key]: { status: 'testing', startTime: Date.now() } }));

    try {
      const start = Date.now();
      let response;

      if (endpoint.isTokenBased) {
        const token = b2bToken || 'test-health-check';
        const url = endpoint.url + token + '/';
        if (endpoint.method === 'POST') {
          response = await axiosInstance.post(url, endpoint.testPayload, { validateStatus: () => true });
        } else {
          response = await axiosInstance.get(url, { validateStatus: () => true });
        }
      } else {
        response = await axiosInstance.get(endpoint.url, { validateStatus: () => true });
      }

      const duration = Date.now() - start;
      const isSuccess = response.status >= 200 && response.status < 500;

      setResults(prev => ({
        ...prev,
        [key]: {
          status: isSuccess ? 'success' : 'error',
          statusCode: response.status,
          duration,
          message: response.data?.message || response.data?.status || (isSuccess ? 'OK' : 'Failed'),
          details: response.data?.error || null,
        },
      }));
    } catch (err) {
      const duration = Date.now() - (results[key]?.startTime || Date.now());
      setResults(prev => ({
        ...prev,
        [key]: {
          status: 'error',
          statusCode: err.response?.status || 0,
          duration,
          message: err.message || 'Connection failed',
          details: err.response?.data?.message || err.response?.data?.error || null,
        },
      }));
    }
  }, [b2bToken, results]);

  const testAll = async () => {
    setTestingAll(true);
    const allEndpoints = ENDPOINT_GROUPS.flatMap(g => g.endpoints);
    await Promise.all(allEndpoints.map(ep => testEndpoint(ep)));
    setTestingAll(false);
  };

  const testCategory = async (category) => {
    const group = ENDPOINT_GROUPS.find(g => g.category === category);
    if (!group) return;
    await Promise.all(group.endpoints.map(ep => testEndpoint(ep)));
  };

  const allEndpoints = ENDPOINT_GROUPS.flatMap(g => g.endpoints);
  const testedCount = allEndpoints.filter(ep => results[ep.url] && results[ep.url].status !== 'testing').length;
  const successCount = allEndpoints.filter(ep => results[ep.url]?.status === 'success').length;
  const errorCount = allEndpoints.filter(ep => results[ep.url]?.status === 'error').length;

  const getStatusBadge = (result) => {
    if (!result) return null;
    if (result.status === 'testing') {
      return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
    }
    if (result.status === 'success') {
      return (
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-emerald-500" />
          <span className="text-xs font-mono text-emerald-600">{result.statusCode}</span>
          <span className="text-xs text-muted-foreground flex items-center gap-0.5">
            <Clock className="h-3 w-3" /> {result.duration}ms
          </span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <XCircle className="h-4 w-4 text-red-500" />
        <span className="text-xs font-mono text-red-600">{result.statusCode || 'ERR'}</span>
        <span className="text-xs text-muted-foreground flex items-center gap-0.5">
          <Clock className="h-3 w-3" /> {result.duration}ms
        </span>
      </div>
    );
  };

  return (
    <div className="px-6 py-8 max-w-[1200px]">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Activity className="h-7 w-7 text-primary" />
          API Health & Documentation
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Test all Shippy API endpoints and view integration documentation.
        </p>
      </div>

      {/* Summary + Actions */}
      <div className="rounded-xl border border-border/70 surface-panel p-5 mb-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            {testedCount > 0 && (
              <>
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">{testedCount}/{allEndpoints.length}</p>
                  <p className="text-xs text-muted-foreground">Tested</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">{successCount}</p>
                  <p className="text-xs text-muted-foreground">Healthy</p>
                </div>
                {errorCount > 0 && (
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{errorCount}</p>
                    <p className="text-xs text-muted-foreground">Failed</p>
                  </div>
                )}
              </>
            )}
            {testedCount === 0 && (
              <p className="text-sm text-muted-foreground">Click "Test All" to check all endpoints at once.</p>
            )}
          </div>
          <Button onClick={testAll} disabled={testingAll}>
            {testingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
            {testingAll ? 'Testing...' : 'Test All Endpoints'}
          </Button>
        </div>
      </div>

      {/* B2B Token Input */}
      <div className="rounded-xl border border-border/70 surface-panel p-4 mb-6 shadow-sm">
        <p className="text-xs font-semibold text-muted-foreground mb-2">API Token (for B2B Packing API & Webhook testing)</p>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Enter API token to test authenticated endpoints..."
            value={b2bToken}
            onChange={(e) => setB2bToken(e.target.value)}
            className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-mono"
          />
        </div>
      </div>

      {/* Endpoint Groups */}
      {ENDPOINT_GROUPS.map(group => (
        <div key={group.category} className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-foreground">{group.category}</h2>
            <Button variant="ghost" size="sm" onClick={() => testCategory(group.category)} className="text-xs h-7">
              <RefreshCw className="h-3 w-3 mr-1" /> Test group
            </Button>
          </div>
          <div className="rounded-xl border border-border overflow-hidden">
            {group.endpoints.map((endpoint, idx) => {
              const result = results[endpoint.url];
              const Icon = endpoint.icon;
              return (
                <div
                  key={endpoint.url}
                  className={`flex items-center justify-between px-4 py-3 ${idx > 0 ? 'border-t border-border' : ''} ${
                    result?.status === 'error' ? 'bg-red-50/50 dark:bg-red-950/10' : result?.status === 'success' ? 'bg-emerald-50/30 dark:bg-emerald-950/10' : 'bg-card'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{endpoint.name}</span>
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${endpoint.method === 'GET' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>
                          {endpoint.method}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{endpoint.description}</p>
                      {result?.details && (
                        <p className="text-xs text-red-500 mt-0.5">{result.details}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(result)}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => testEndpoint(endpoint)}
                      disabled={result?.status === 'testing'}
                    >
                      {result?.status === 'testing' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Test'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* ================================================================= */}
      {/* API DOCUMENTATION                                                 */}
      {/* ================================================================= */}
      <div className="mt-10 mb-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          API Documentation
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Reference for external integrations.</p>
      </div>

      <div className="space-y-4">
        {/* ---- B2B Packing API ----------------------------------------- */}
        <DocSection title="B2B Packing API" icon={Package} defaultOpen>
          <div className="space-y-4">
            {/* Endpoint */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Endpoint</p>
              <CodeBlock code={`POST ${API_BASE}/api/v1/packing/shippy-pack-sim-8f7a3b/{token}/`} />
            </div>

            <p className="text-xs text-muted-foreground">
              Token-based authentication via URL path. No headers required beyond <code className="px-1 py-0.5 rounded bg-muted font-mono text-[11px]">Content-Type: application/json</code>.
            </p>

            {/* Request params */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Request Body Parameters</p>
              <ParamTable params={[
                { name: 'items', type: 'array', required: true, desc: 'Array of { variant_id, quantity }. variant_id is the Shopify variant ID.' },
                { name: 'country', type: 'string', required: false, desc: 'Destination country name (e.g. "Netherlands"). Defaults to "Belgium".' },
                { name: 'postal_code', type: 'string', required: false, desc: 'Postal/ZIP code for zone-specific pricing (islands, remote areas).' },
                { name: 'order_total', type: 'number', required: false, desc: 'Order total in EUR. Enables franco (free shipping) threshold check.' },
                { name: 'item_costs', type: 'object', required: false, desc: 'Unit costs per variant_id, e.g. {"44571...": 15.00}. Enables margin-based franco.' },
              ]} />
            </div>

            {/* Example request */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Example Request</p>
              <CodeBlock code={`curl -X POST '${API_BASE}/api/v1/packing/shippy-pack-sim-8f7a3b/{token}/' \\
  -H 'Content-Type: application/json' \\
  -d '{
  "items": [
    { "variant_id": "44571234567890", "quantity": 2 },
    { "variant_id": "44571234567891", "quantity": 1 }
  ],
  "country": "Netherlands",
  "postal_code": "1012",
  "order_total": 750.00,
  "item_costs": {
    "44571234567890": 15.00,
    "44571234567891": 22.50
  }
}'`} />
            </div>

            {/* Example response */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Example Response</p>
              <CodeBlock code={JSON.stringify({
                success: true,
                total_items: 3,
                total_bins: 1,
                estimated_shipping_cost: 0.0,
                original_shipping_cost: 18.40,
                packaging_cost: 1.35,
                total_cost: 1.35,
                currency: 'EUR',
                country: 'Netherlands',
                postal_code: '1012',
                franco: {
                  threshold: 215.0,
                  order_total: 750.0,
                  applied: true,
                  partial: false,
                  customer_charge: 0,
                  savings: 18.40,
                  margin_based: true,
                  margin_info: {
                    total_item_cost: 52.50,
                    margin_with_franco: 678.75,
                    margin_pct_with_franco: 90.5,
                    margin_without_franco: 697.15,
                    margin_pct_without_franco: 92.9,
                    min_margin_required: 21.5,
                  },
                },
                bins_summary: { 'CB-50x50x50': 1 },
                bins_detail: [
                  {
                    bin_name: 'CB-50x50x50',
                    items_count: 3,
                    utilization: 62.4,
                    packaging_cost: 1.35,
                    weight: 7.50,
                  },
                ],
                dimension_coverage: {
                  direct: 3,
                  product_fallback: 0,
                  brand_average: 0,
                  global_average: 0,
                  default: 0,
                  total: 3,
                },
                processing_time_ms: 124.5,
                warnings: [],
              }, null, 2)} />
            </div>

            {/* Key response fields */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Key Response Fields</p>
              <ParamTable params={[
                { name: 'estimated_shipping_cost', type: 'number', required: false, desc: 'Final shipping cost after franco deductions (what to charge the customer).' },
                { name: 'original_shipping_cost', type: 'number', required: false, desc: 'Shipping cost before any franco deduction.' },
                { name: 'packaging_cost', type: 'number', required: false, desc: 'Cost of packaging materials (always charged, even with franco).' },
                { name: 'total_cost', type: 'number', required: false, desc: 'estimated_shipping_cost + packaging_cost.' },
                { name: 'franco.applied', type: 'boolean', required: false, desc: 'True if full free shipping was applied.' },
                { name: 'franco.partial', type: 'boolean', required: false, desc: 'True if partial franco: customer pays a reduced amount to maintain minimum margin.' },
                { name: 'franco.customer_charge', type: 'number', required: false, desc: 'Amount to charge the customer for shipping (when partial franco).' },
                { name: 'bins_summary', type: 'object', required: false, desc: 'Box types used and their count, e.g. {"CB-50x50x50": 1}.' },
                { name: 'dimension_coverage.direct', type: 'number', required: false, desc: 'Items with exact dimensions from packaging DB.' },
                { name: 'zone', type: 'object', required: false, desc: 'Present when postal code matched a special shipping zone (islands, etc.).' },
              ]} />
            </div>
          </div>
        </DocSection>

        {/* ---- Bpost Checker API ---------------------------------------- */}
        <DocSection title="Bpost Checker API" icon={Truck}>
          <div className="space-y-4">
            {/* Endpoint */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Endpoint</p>
              <CodeBlock code={`POST ${API_BASE}/api/v1/packing/bpost-check/{token}/`} />
            </div>

            <p className="text-xs text-muted-foreground">
              Check whether a product can be shipped via Bpost. Accepts an <strong>Odoo product ID</strong> and returns eligibility
              status, surcharges, and dimension details. Uses the same token as the B2B Packing API.
            </p>

            {/* Request params */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Request Body Parameters</p>
              <ParamTable params={[
                { name: 'product_id', type: 'integer', required: false, desc: 'Single product mode: Odoo product ID. Cannot be combined with items.' },
                { name: 'items', type: 'array', required: false, desc: 'Multi-product mode: [{"product_id": 123, "quantity": 2}, ...]. Runs packing simulation.' },
                { name: 'country', type: 'string', required: false, desc: 'ISO2 code or country name. Defaults to "BE". Surcharge rules differ per country.' },
              ]} />
            </div>

            {/* Example request */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Example Request</p>
              <CodeBlock code={`# Single product
curl -X POST '${API_BASE}/api/v1/packing/bpost-check/{token}/' \\
  -H 'Content-Type: application/json' \\
  -d '{ "product_id": 275945, "country": "BE" }'

# Multiple products (runs packing simulation)
curl -X POST '${API_BASE}/api/v1/packing/bpost-check/{token}/' \\
  -H 'Content-Type: application/json' \\
  -d '{
  "items": [
    { "product_id": 275945, "quantity": 2 },
    { "product_id": 285601, "quantity": 1 }
  ],
  "country": "BE"
}'`} />
            </div>

            {/* Example response — eligible, no surcharges */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Example Response — Eligible</p>
              <CodeBlock code={JSON.stringify({
                product_id: 275945,
                product_name: 'Uni-Bright Smart controller RGBCW',
                bpost_eligible: true,
                surcharges: [],
                surcharge_total: 0.0,
                details: 'Fits within Bpost limits — no surcharges',
                boxes: {
                  box_number: 1,
                  dimensions: { width: 6.5, height: 9.5, length: 4.0, weight: 1.0 },
                  longest_side: 9.5,
                  girth: 30.5,
                  sum_sides: 20.0,
                  eligible: true,
                  surcharge: 0,
                },
              }, null, 2)} />
            </div>

            {/* Example response — with surcharge */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Example Response — Surcharge</p>
              <CodeBlock code={JSON.stringify({
                product_id: 285601,
                product_name: '10 HEURES 10 Zigouzi Pendant Lamp (Black, Ø150 cm)',
                bpost_eligible: true,
                surcharges: ['Girth>300'],
                surcharge_total: 68.30,
                details: 'Eligible with surcharges: Girth>300 (+€68.30)',
                boxes: {
                  box_number: 1,
                  dimensions: { width: 127.0, height: 11.0, length: 127.0, weight: 3.5 },
                  longest_side: 127.0,
                  girth: 403.0,
                  sum_sides: 265.0,
                  eligible: true,
                  surcharge: 68.3,
                  surcharge_labels: ['Girth>300'],
                },
              }, null, 2)} />
            </div>

            {/* Example response — multi-product */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Example Response — Multi-Product (Packing Simulation)</p>
              <CodeBlock code={JSON.stringify({
                country: 'Belgium',
                country_iso2: 'BE',
                total_items: 3,
                total_parcels: 2,
                bpost_eligible: true,
                surcharges: ['L>150cm'],
                surcharge_total: 68.30,
                details: 'Eligible with surcharges on 1/2 parcels (+€68.30 total)',
                items: [
                  { product_id: 275945, product_name: 'Uni-Bright Smart controller RGBCW', quantity: 2 },
                  { product_id: 285601, product_name: '10 HEURES 10 Zigouzi Pendant Lamp', quantity: 1 },
                ],
                parcels: [
                  {
                    bin_name: 'CB-40x40x40',
                    items_count: 2,
                    dimensions: { width: 40, height: 40, length: 40, weight: 2.0 },
                    longest_side: 40, girth: 200, sum_sides: 120,
                    eligible: true, surcharge: 0,
                  },
                  {
                    bin_name: 'Direct Shipping',
                    items_count: 1,
                    dimensions: { width: 127, height: 11, length: 127, weight: 3.5 },
                    longest_side: 127, girth: 403, sum_sides: 265,
                    eligible: true, surcharge: 68.30, surcharge_labels: ['L>150cm'],
                  },
                ],
              }, null, 2)} />
            </div>

            {/* Example response — not eligible (pallet) */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Example Response — Not Eligible</p>
              <CodeBlock code={JSON.stringify({
                product_id: 300123,
                product_name: 'Large Outdoor Table (200x100cm)',
                bpost_eligible: false,
                surcharges: [],
                surcharge_total: 0,
                details: 'Product is flagged as shipped_on_pallet — not suitable for Bpost parcel',
                boxes: {
                  box_number: 1,
                  dimensions: { width: 200.0, height: 100.0, length: 80.0, weight: 45.0 },
                  longest_side: 200.0,
                  girth: 560.0,
                  sum_sides: 380.0,
                  eligible: false,
                  reason: 'shipped_on_pallet',
                },
              }, null, 2)} />
            </div>

            {/* Example response — product not found */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Example Response — Product Not Found (404)</p>
              <CodeBlock code={JSON.stringify({
                product_id: 999999,
                error: 'Product not found or missing dimensions',
                bpost_eligible: null,
              }, null, 2)} />
            </div>

            {/* Key response fields */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Key Response Fields</p>
              <ParamTable params={[
                { name: 'bpost_eligible', type: 'boolean', required: false, desc: 'true = can ship with Bpost, false = refused or pallet-only, null = product not found.' },
                { name: 'surcharges', type: 'array', required: false, desc: 'List of surcharge labels that apply (e.g. ["L>150cm", "Girth>300"]).' },
                { name: 'surcharge_total', type: 'number', required: false, desc: 'Total surcharge amount in EUR.' },
                { name: 'details', type: 'string', required: false, desc: 'Human-readable summary of the result.' },
                { name: 'boxes', type: 'object|array', required: false, desc: 'Dimension details per box. Single object for 1-box products, array for multi-box.' },
                { name: 'boxes.longest_side', type: 'number', required: false, desc: 'Longest dimension in cm. Bpost surcharge triggers at >150cm (BE).' },
                { name: 'boxes.girth', type: 'number', required: false, desc: 'L + 2×(W+H) in cm. Bpost surcharge triggers at >300cm (BE).' },
                { name: 'boxes.sum_sides', type: 'number', required: false, desc: 'L+W+H in cm. Bpost surcharge triggers at >150cm / >200cm (non-BE).' },
              ]} />
            </div>

            {/* Bpost rules reference */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Bpost Surcharge Rules</p>
              <div className="rounded-lg bg-muted/50 p-3 text-xs space-y-1.5">
                <p className="text-muted-foreground"><strong className="text-foreground">Max weight:</strong> 30 kg — heavier items are flagged as not eligible</p>
                <p className="text-muted-foreground"><strong className="text-foreground">Belgium:</strong> L {'>'} 150cm OR Girth {'>'} 300cm → €68.30 flat surcharge</p>
                <p className="text-muted-foreground"><strong className="text-foreground">Other countries:</strong> L+W+H {'>'} 150cm → €7.13 | L+W+H {'>'} 200cm → €59.38</p>
                <p className="text-muted-foreground"><strong className="text-foreground">Pallet products:</strong> Automatically flagged as not eligible</p>
              </div>
            </div>
          </div>
        </DocSection>

        {/* ---- Return Rate API ------------------------------------------ */}
        <DocSection title="Return Rate API" icon={Euro}>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Endpoint</p>
              <CodeBlock code={`POST ${API_BASE}/api/v1/packing/return-rate/{token}/`} />
            </div>

            <p className="text-xs text-muted-foreground">
              Simulate a <strong>return</strong> and price it for every available carrier (<strong>GLS and bpost</strong>), <strong>VAT included</strong> — customer-facing (e.g. a return portal). Same input as the Bpost Checker and the <strong>same token</strong> as the B2B Packing API. No box is added: a return goes back in outer boxes the customer has, as few parcels as the items need (never more than max_parcels). With order_number those are the cartons the order shipped in, plus the own box of an item that shipped direct; an item that came inside a carton goes back in a carton, so the label never lands on a manufacturer's box. Book one label per returned parcel; each parcel gives its box (own box / original carton), outer_box (true; false = no original carton left for it, see note; null = order unknown), weight, dimensions and items (product_id, quantity, and box_number of boxes_per_item for products that ship in several boxes). Each parcel is priced per carrier with its return surcharges (Garantie / fuel, manual handling, GLS extra grand/lourd). GLS return prices follow the outbound GLS card (France band minus EUR 0.50, Belgium BusinessParcel minus EUR 1.00, rest of the EU at the band); bpost uses its own return grid. EU only.
            </p>

            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Request Body Parameters</p>
              <ParamTable params={[
                { name: 'product_id', type: 'integer', required: false, desc: 'Single product mode: Odoo product ID. Cannot be combined with items.' },
                { name: 'items', type: 'array', required: false, desc: 'Multi-product mode: [{"product_id": 123, "quantity": 2}, ...]. Packed into the boxes the customer has (see order_number), never into a new carton.' },
                { name: 'country', type: 'string', required: false, desc: 'ISO2 code or country name. Defaults to "BE". EU only. Rates, surcharges and VAT differ per country.' },
                { name: 'postal_code', type: 'string', required: false, desc: 'Sender ZIP (aliases: zip, zipcode). Echoed + logged. Return rates are country-flat today, so it does not change the price yet (reserved for island/remote return zones).' },
                { name: 'order_number', type: 'string', required: false, desc: 'Shopify order the items came from (e.g. "#202031"). Lets Shippy also use the cartons the order shipped in: returned items go back in as few boxes as they need (their own boxes or those cartons). Response: order_found, original_cartons.' },
                { name: 'max_parcels', type: 'integer', required: false, desc: 'Most parcels the customer can send (e.g. the package count of the original Odoo delivery, or fewer if the customer wants). Returned items need more boxes: options is empty, needed_parcels says how many and details why.' },
              ]} />
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Example Request</p>
              <CodeBlock code={`# Single product
curl -X POST '${API_BASE}/api/v1/packing/return-rate/{token}/' \\
  -H 'Content-Type: application/json' \\
  -d '{ "product_id": 275945, "country": "BE", "postal_code": "1000" }'

# Multiple products from an order that left in 2 parcels
curl -X POST '${API_BASE}/api/v1/packing/return-rate/{token}/' \\
  -H 'Content-Type: application/json' \\
  -d '{
  "items": [ { "product_id": 275945, "quantity": 2 } ],
  "country": "BE",
  "order_number": "#202031",
  "max_parcels": 2
}'`} />
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Example Response</p>
              <CodeBlock code={JSON.stringify({
                country: 'Belgium', country_iso2: 'BE', postal_code: '1000', vat_rate: 0.21,
                total_items: 1, total_parcels: 1,
                parcels: [{ bin_name: 'CB-40x30x20', weight: 3.0, dimensions: { width: 40, height: 30, length: 20 } }],
                options: [
                  { carrier: 'GLS Belgium (BusinessParcel)', carrier_id: 24, eligible: true, cost_excl_vat: 4.66, cost_incl_vat: 5.64, surcharge_total: 0, surcharge_labels: [], per_parcel: [{ weight: 3.0, base_rate: 4.05, cost_excl_vat: 4.66, surcharge: 0 }] },
                  { carrier: 'BPost (Default)', carrier_id: 1, eligible: true, cost_excl_vat: 7.33, cost_incl_vat: 8.87, surcharge_total: 0, surcharge_labels: [], per_parcel: [{ weight: 3.0, base_rate: 6.24, cost_excl_vat: 7.33, surcharge: 0 }] },
                ],
                cheapest: { carrier: 'GLS Belgium (BusinessParcel)', cost_incl_vat: 5.64 },
              }, null, 2)} />
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Key Response Fields</p>
              <ParamTable params={[
                { name: 'options', type: 'array', required: false, desc: 'One entry per carrier (GLS + bpost), sorted cheapest-first.' },
                { name: 'options[].cost_incl_vat', type: 'number', required: false, desc: 'Total return cost incl. VAT — the customer-facing number.' },
                { name: 'options[].surcharge_labels', type: 'array', required: false, desc: 'Which return surcharges applied (e.g. ["Manual Handling (>100cm)"]).' },
                { name: 'options[].eligible', type: 'boolean', required: false, desc: 'false if a parcel is refused (too big/heavy) or has no rate; then `reason` explains.' },
                { name: 'cheapest', type: 'object', required: false, desc: 'The cheapest eligible option: { carrier, cost_incl_vat }.' },
                { name: 'vat_rate', type: 'number', required: false, desc: 'Destination VAT rate applied (e.g. 0.21 for BE, 0.20 for FR).' },
              ]} />
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Return Surcharges</p>
              <div className="rounded-lg bg-muted/50 p-3 text-xs space-y-1.5">
                <p className="text-muted-foreground"><strong className="text-foreground">bpost:</strong> Garantie €0.81/parcel; fuel (BE €0.28 fixed / intl 13.5%); Manual Handling €2.56 when any side {'>'} 100cm</p>
                <p className="text-muted-foreground"><strong className="text-foreground">GLS:</strong> fuel + regulatory; extra grand (+€1, {'>'} 126 dm³) / extra lourd (+€3.74, {'>'} 25 kg)</p>
                <p className="text-muted-foreground"><strong className="text-foreground">GLS from France:</strong> billed on the higher of real and volumetric weight (L×W×H / 6000, 1 kg tolerance, see <code className="bg-background px-1 rounded">billed_weight</code>); parcels over 120 cm long, 150 cm L+W+H or 30 kg are not eligible. GLS does not take returns from Greece.</p>
                <p className="text-muted-foreground"><strong className="text-foreground">EU only:</strong> BE uses GLS Belgium / bpost national; other EU uses GLS France / bpost World Easy Return. Both carriers returned; pick per <code className="bg-background px-1 rounded">cost_incl_vat</code>.</p>
              </div>
            </div>
          </div>
        </DocSection>

        {/* ---- Shopify Flow Webhook ------------------------------------ */}
        <DocSection title="Shopify Flow Webhook" icon={Webhook}>
          <div className="space-y-4">
            {/* Endpoint */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Endpoint</p>
              <CodeBlock code={`POST ${API_BASE}/api/webhook/packing-analysis/{token}/`} />
            </div>

            <p className="text-xs text-muted-foreground">
              Triggered by Shopify Flow on every new order. Runs the full packing analysis and sends a Slack alert if the order
              has a shipping loss {'>'} $50 or gross margin {'<'} 10%.
            </p>

            {/* Request params */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Request Body Parameters</p>
              <ParamTable params={[
                { name: 'order_number', type: 'string', required: true, desc: 'Shopify order name, e.g. "#97656" or "97656" (# prefix optional).' },
              ]} />
            </div>

            {/* Shopify Flow config */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Shopify Flow Configuration</p>
              <div className="rounded-lg bg-muted/50 p-4 space-y-2 text-xs">
                <div className="flex gap-2">
                  <span className="font-semibold text-foreground w-20 shrink-0">Action:</span>
                  <span className="text-muted-foreground">Send HTTP request</span>
                </div>
                <div className="flex gap-2">
                  <span className="font-semibold text-foreground w-20 shrink-0">Method:</span>
                  <span className="font-mono text-muted-foreground">POST</span>
                </div>
                <div className="flex gap-2">
                  <span className="font-semibold text-foreground w-20 shrink-0">URL:</span>
                  <span className="font-mono text-muted-foreground break-all">{API_BASE}/api/webhook/packing-analysis/&#123;token&#125;/</span>
                </div>
                <div className="flex gap-2">
                  <span className="font-semibold text-foreground w-20 shrink-0">Headers:</span>
                  <span className="font-mono text-muted-foreground">Content-Type: application/json</span>
                </div>
                <div className="flex gap-2">
                  <span className="font-semibold text-foreground w-20 shrink-0">Body:</span>
                  <span className="font-mono text-muted-foreground">{'{"order_number": "{{order.name}}"}'}</span>
                </div>
              </div>
            </div>

            {/* Example request */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Example Request</p>
              <CodeBlock code={`curl -X POST '${API_BASE}/api/webhook/packing-analysis/{token}/' \\
  -H 'Content-Type: application/json' \\
  -d '{"order_number": "#97656"}'`} />
            </div>

            {/* Example response */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Example Response</p>
              <CodeBlock code={JSON.stringify({
                success: true,
                order_number: '#97656',
                result: {
                  status: 'success',
                  order_number: '#97656',
                  analysis: {
                    actual_shipping_cost: 90.40,
                    simulated_shipping_cost: 149.50,
                    cost_difference: -59.10,
                    variance_percent: -65.38,
                    money_lost: 59.10,
                    alert_triggered: true,
                    threshold_eur: 50.0,
                  },
                  packing_solution: {
                    description: 'Hybrid: 2 optimal bins',
                    total_bins: 2,
                    bin_types: { 'CB-50x50x50': 1, 'CB-45x45x25': 1 },
                    total_cost: 149.50,
                  },
                  slack_sent: true,
                },
              }, null, 2)} />
            </div>

            {/* Key response fields */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Key Response Fields</p>
              <ParamTable params={[
                { name: 'result.analysis.actual_shipping_cost', type: 'number', required: false, desc: 'What the customer was charged for shipping.' },
                { name: 'result.analysis.simulated_shipping_cost', type: 'number', required: false, desc: 'Optimal shipping cost calculated by Shippy.' },
                { name: 'result.analysis.money_lost', type: 'number', required: false, desc: 'Shipping loss (simulated - actual). Triggers alert when > 50.' },
                { name: 'result.analysis.alert_triggered', type: 'boolean', required: false, desc: 'True if shipping loss > 50 or margin < 10%.' },
                { name: 'result.packing_solution.bin_types', type: 'object', required: false, desc: 'Box types and counts in optimal solution.' },
                { name: 'result.slack_sent', type: 'boolean', required: false, desc: 'True if a Slack alert was sent for this order.' },
              ]} />
            </div>

            {/* Alert triggers */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Slack Alert Triggers</p>
              <div className="rounded-lg bg-muted/50 p-4 text-xs space-y-1.5 text-muted-foreground">
                <p><span className="font-semibold text-foreground">Shipping Loss Alert:</span> Triggered when simulated shipping cost exceeds customer-paid amount by more than $50.</p>
                <p><span className="font-semibold text-foreground">Margin Danger Alert:</span> Triggered when gross margin (after shipping) drops below 10%.</p>
                <p><span className="font-semibold text-foreground">Duplicate Prevention:</span> Same order won't be processed twice within 24 hours (cached).</p>
              </div>
            </div>
          </div>
        </DocSection>
      </div>
    </div>
  );
};

export default ApiHealth;
